import React, { ReactElement, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import enumStrings, { RoutePaths } from "../../../enumStrings";
import { commonCognitoPasswordRegex } from "../../common/utilities/commonPasswordRegex";
import PasswordField from "../../common/PasswordField/PasswordField";
import {
  UserType,
  useSetHasSignedUpForChamberMemberMutation,
} from "../../../graphql/types";
import { confirmSignIn, fetchAuthSession, signIn } from "aws-amplify/auth";
import BaseAuthPage from "./BaseAuthPage";
import { commonStyles } from "../../common/commonStyles";
import LinkText from "../../common/LinkText";
import Button from "../../common/Button";

interface NewPasswordInput {
  newPassword: string;
  confirmPassword: string;
}

const NewPassword = (): ReactElement => {
  const initialValues: NewPasswordInput = {
    newPassword: "",
    confirmPassword: "",
  };

  const validationSchema = yup.object({
    newPassword: yup
      .string()
      .required(enumStrings.pages.common.validation.newPassword)
      .matches(
        commonCognitoPasswordRegex,
        enumStrings.pages.common.validation.mustContain8Characters
      ),
    confirmPassword: yup
      .string()
      .oneOf(
        [yup.ref("newPassword"), undefined],
        enumStrings.pages.common.validation.passwordsMustMatch
      ),
  });

  const [customError, setCustomError] = useState("");
  const { email, tempPassword } = JSON.parse(
    sessionStorage?.getItem(
      enumStrings.sessionStorageKey.loggedInWithTemporaryPassword
    ) || `{"email":"", "tempPassword": ""}`
  );
  const [loader, setLoader] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);

  const [setHasSignedUpForChamberMember] =
    useSetHasSignedUpForChamberMemberMutation();

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (input: NewPasswordInput) => {
      setCustomError("");
      setLoader(true);
      try {
        try {
          const user = await signIn({
            username: email,
            password: tempPassword,
          });
          if (
            user.nextStep.signInStep ===
            "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
          ) {
            await confirmSignIn({
              challengeResponse: input.confirmPassword,
            });

            const authSession = await fetchAuthSession();

            if (
              authSession &&
              authSession.tokens &&
              authSession.tokens.accessToken &&
              authSession.tokens.accessToken.payload &&
              // @ts-ignore
              authSession.tokens.accessToken.payload["cognito:groups"][0]
            ) {
              const userType =
                // @ts-ignore
                authSession.tokens.accessToken.payload["cognito:groups"][0];
              const customAccessTokenForSettingHasSignedUp =
                authSession.tokens.accessToken.toString();

              if (userType === UserType.ChamberMemberAdmin) {
                await setHasSignedUpForChamberMember({
                  context: {
                    customAccessTokenForSettingHasSignedUp,
                  },
                });
              }
            }

            sessionStorage.removeItem(
              enumStrings.sessionStorageKey.loggedInWithTemporaryPassword
            );
            window.location.href = RoutePaths.home;
          }
        } catch (error) {
          setLoader(false);
          setButtonDisabled(false);
        }
        setButtonDisabled(true);
      } catch (error: any) {
        if (error?.name === "NetworkError") {
          setCustomError(enumStrings.pages.common.errors.networkError);
        }
        setLoader(false);
        setButtonDisabled(false);
      }
    },
  });

  return (
    <BaseAuthPage logoSubtitle={enumStrings.pages.newPassword.logoSubtitle}>
      <form
        style={{
          width: "100%",
        }}
        onSubmit={formik.handleSubmit}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            gap: 3,
          }}
          onSubmit={() => formik.handleSubmit()}
        >
          <Typography
            sx={{
              fontSize: commonStyles.fontSizes.medium,
              pb: 2,
            }}
          >
            {enumStrings.pages.newPassword.description}
          </Typography>
          <PasswordField
            id="newPassword"
            fieldName="newPassword"
            label="New Password"
            value={formik.values.newPassword}
            onChange={formik.handleChange}
            error={formik.errors.newPassword}
            touched={formik.touched.newPassword}
          />
          <PasswordField
            id="confirmPassword"
            fieldName="confirmPassword"
            label="Confirm Password"
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            error={formik.errors.confirmPassword}
            touched={formik.touched.confirmPassword}
          />
          <Button
            text={enumStrings.pages.newPassword.buttonText}
            disabled={buttonDisabled || loader}
            isLoading={loader}
            fullWidth
            size="large"
            type="submit"
            errorText={customError}
            additionalSx={{
              mt: 4,
            }}
          />
          <LinkText
            to={RoutePaths.login}
            text={enumStrings.pages.common.buttons.goBackToLoginText}
          />
        </Box>
      </form>
    </BaseAuthPage>
  );
};

export default NewPassword;
